/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { isNotNil } from '@gh/core-util';
import { Item, Layer } from 'paper';
import {
  GraphicalTakeOffImageTransformPipeline,
  GraphicalTakeOffViewTransform,
} from '../../resources';
import { applyRasterPipeline, newRaster } from './canvas-rasterize.utils';
import {
  GraphicalTakeOffElementBase,
  paperEffect,
  PaperElementEffect,
} from './graphical-take-off-element';

const getRaster = (item: Item) => item['$raster'];
const setRaster = (item: Item, raster: Item) => {
  item['$raster'] = raster;
};
const removeRaster = (item: Item) => {
  item['$raster'] = void 0;
};

const imagePaperEffects: PaperElementEffect[] = [
  paperEffect(
    ['image', 'pipeline'],
    (item: Item,
     [image, pipeline]: [HTMLImageElement, GraphicalTakeOffImageTransformPipeline],
     [previousImage, previousPipeline]: [HTMLImageElement, GraphicalTakeOffImageTransformPipeline]) => {
      if (image !== previousImage) {
        item.removeChildren();
        removeRaster(item);

        if (isNotNil(image)) {
          const raster = newRaster(image, pipeline);
          const rasterWithEffects = applyRasterPipeline(raster, image, pipeline);
          item.addChild(rasterWithEffects);
          setRaster(item, rasterWithEffects);
        }
      } else if (image && pipeline !== previousPipeline) {
        const raster = getRaster(item);
        const rasterWithEffects = applyRasterPipeline(raster, image, pipeline, previousPipeline);
        item.addChild(rasterWithEffects);
        setRaster(item, rasterWithEffects);
      }
    }),
];

@Component({
  standalone: false,
  selector: 'gh-graphical-take-off-canvas-transformed-image',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GraphicalTakeOffCanvasTransformedImageComponent extends GraphicalTakeOffElementBase {
  @Input() image: HTMLImageElement;
  @Input() pipeline: GraphicalTakeOffImageTransformPipeline;
  @Input() viewTransform: GraphicalTakeOffViewTransform;

  constructor() {
    super(imagePaperEffects);
  }

  protected createPaperItem(): Item {
    const layer = new Layer({
      data: {
        id: 'rasterLayer',
        type: 'raster-layer',
      },
    });
    layer.sendToBack();
    return layer;
  }
}
